/**
 * ChatFullScreen - Interfaccia chat a schermo intero
 *
 * Si espande dal mini-modal per conversazioni complete con l'AI.
 * Mantiene il contesto della vista corrente per comandi contestuali.
 *
 * Architettura:
 * - Chat: interazioni conversazionali con l'utente
 * - Agents: esecuzione azioni (delegati dalla chat quando serve)
 *
 * Pattern Neurale:
 * - Ogni messaggio è cliccabile → apre DetailPanel
 * - Ogni messaggio ha FastActions → crea connessioni neurali
 * @updated 2026-01-10
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import {
  Paperclip,
  Mic,
  MicOff,
  Send,
  Loader2,
  Sparkles,
} from 'lucide-react';
import { useChatStore } from '@business-assistant/shared/services';
import type { ChatMessage } from '@business-assistant/shared';
import type { ViewType } from '../../types/appTypes';
import { ChatMessageBubble } from './ChatMessageBubble';
import { MessageDetailPanel } from './MessageDetailPanel';
import './chat-fullscreen.css';

export interface ChatContext {
  view: ViewType;
  viewTitle: string;
  activeFilter?: string;
  selectedEntityId?: string;
  selectedEntityType?: string;
}

interface ChatFullScreenProps {
  isOpen: boolean;
  onClose: () => void;
  context: ChatContext;
  // Input state (lifted from parent)
  inputText: string;
  onInputChange: (text: string) => void;
  onSend: () => void;
  // Recording state
  isRecording: boolean;
  recordingTime: number;
  onToggleRecording: () => void;
  // Processing state
  isProcessing: boolean;
  // Services status
  whisperAvailable: boolean | null;
  // File handling
  onFileAttach: () => void;
}

export function ChatFullScreen({
  isOpen,
  onClose,
  context,
  inputText,
  onInputChange,
  onSend,
  isRecording,
  recordingTime,
  onToggleRecording,
  isProcessing,
  whisperAvailable,
  onFileAttach,
}: ChatFullScreenProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // State for message detail panel
  const [selectedMessage, setSelectedMessage] = useState<ChatMessage | null>(null);

  // Chat store
  const { sessions, activeSessionId, streamingText } = useChatStore();
  const activeSession = sessions.find(s => s.id === activeSessionId);
  const messages = activeSession?.messages || [];

  // Handle message click - open detail panel
  const handleMessageClick = useCallback((message: ChatMessage) => {
    setSelectedMessage(message);
  }, []);

  // Handle fast action on message
  const handleMessageAction = useCallback((action: string, message: ChatMessage) => {
    console.log(`FastAction: ${action} on message:`, message.id);
    // TODO: Implement action handlers
    // - forward: open share/forward modal
    // - task: open TaskWidget with message context
    // - note: open NoteWidget with message context
    // - event: open EventWidget with message context

    // For now, open detail panel to show connections
    setSelectedMessage(message);
  }, []);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingText]);

  // Focus textarea when opened
  useEffect(() => {
    if (isOpen && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isOpen]);

  // Format recording time
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };

  // Handle key press
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  }, [onSend]);

  if (!isOpen) return null;

  return (
    <div className="chat-fullscreen">
      {/* Messages area */}
      <div className="chat-fullscreen__messages">
        {messages.length === 0 && !streamingText && (
          <div className="chat-fullscreen__empty">
            <Sparkles size={32} />
            <p>How can I help you?</p>
            <p className="chat-fullscreen__empty-hint">I&apos;m in the context of <strong>{context.viewTitle}</strong>. Ask me anything about what you&apos;re viewing.</p>
          </div>
        )}

        {messages.map((msg) => (
          <ChatMessageBubble
            key={msg.id}
            message={msg}
            onClick={handleMessageClick}
            onAction={handleMessageAction}
            connectionCount={0} // TODO: get from neural connections store
            compact={true}
          />
        ))}

        {/* Streaming response */}
        {streamingText && (
          <div className="chat-message chat-message--assistant chat-message--streaming">
            <p className="chat-message__content">{streamingText}</p>
            <span className="chat-message__badge">responding...</span>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input area */}
      <div className="chat-fullscreen__input-area">
        <div className="chat-fullscreen__input-box">
          <textarea
            ref={textareaRef}
            value={inputText}
            onChange={(e) => onInputChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={isRecording ? 'Speak now...' : 'Write a message...'}
            disabled={isProcessing || isRecording}
            rows={1}
          />
          <div className="chat-fullscreen__tools">
            <button
              className="chat-tool-btn"
              onClick={onFileAttach}
              disabled={isProcessing}
              aria-label="Attach file"
            >
              <Paperclip size={18} />
            </button>
            <button
              className={`chat-tool-btn ${isRecording ? 'recording' : ''}`}
              onClick={onToggleRecording}
              disabled={isProcessing && !isRecording}
              aria-label={isRecording ? 'Stop recording' : 'Record'}
            >
              {isRecording ? (
                <>
                  <MicOff size={18} />
                  <span className="recording-time">{formatTime(recordingTime)}</span>
                </>
              ) : (
                <Mic size={18} />
              )}
            </button>
            <button
              className="chat-send-btn"
              onClick={onSend}
              disabled={isProcessing || isRecording || !inputText.trim()}
              aria-label="Send"
            >
              {isProcessing ? <Loader2 size={18} className="spin" /> : <Send size={18} />}
            </button>
          </div>
        </div>
      </div>

      {/* Message Detail Panel */}
      <MessageDetailPanel
        message={selectedMessage}
        isOpen={!!selectedMessage}
        onClose={() => setSelectedMessage(null)}
      />
    </div>
  );
}
